/**
 * E-signature: the rules every side has to agree on.
 *
 * A generated document is sent out by link; each signer draws or types a
 * signature on a public page; the signed PDF, with an audit page, comes back.
 * This file holds what the API, the nightly cron, the signing page and the two
 * sender UIs must answer identically: who may use it, what a `{SIGN…}`
 * placeholder is, what state a request is in *right now*, and what a signer is
 * allowed to see.
 *
 * It is an electronic signature with an audit trail — who, when, from where, on
 * which exact document. It is not a certificate-based digital signature, and
 * nothing here should be worded as if it were.
 */

import { MS_PER_DAY } from './plan/constants'
import { effectiveRecordMode, isCombinedMode } from './combined'
import type { ListPagination } from './portalLists'
import { missingFieldOfWarning } from './worker'

// =============================================================================
// Constants
// =============================================================================

/** Days a signer has, counted from the moment the request is sent. */
export const SIGNATURE_VALIDITY_DAYS = 14

/** Wrong passwords a link survives. The next one locks it until the sender re-issues it. */
export const SIGNATURE_MAX_PASSWORD_ATTEMPTS = 5

/**
 * Days a closed request keeps its files (master, previews, signature images,
 * final PDF). They hold handwriting and IP addresses, so they are not kept
 * forever; the signed PDF has by then been delivered to the sender and, where
 * configured, saved on the record. The rows — the audit trail — stay.
 */
export const SIGNATURE_FILE_RETENTION_DAYS = 90

/** Most signers one request can have. A contract has a handful; a hundred is abuse. */
export const SIGNATURE_MAX_SIGNERS = 10

/** Sender-set password: long enough to be worth typing, short enough to dictate. */
export const SIGNATURE_PASSWORD_MIN_LENGTH = 6
export const SIGNATURE_PASSWORD_MAX_LENGTH = 128

/** Free-text limits on what a stranger can make us store. */
export const SIGNATURE_NAME_MAX_LENGTH = 120
export const SIGNATURE_NOTE_MAX_LENGTH = 200
export const SIGNATURE_REASON_MAX_LENGTH = 500
export const SIGNATURE_TITLE_MAX_LENGTH = 200

/**
 * The signature picture a signer uploads. The signing page draws at most
 * 1200×400; the caps leave room for a high-density phone and nothing more.
 */
export const SIGNATURE_PNG_MAX_BYTES = 300 * 1024
export const SIGNATURE_PNG_MAX_WIDTH = 1600
export const SIGNATURE_PNG_MAX_HEIGHT = 800
export const SIGNATURE_PNG_MIN_WIDTH = 40
export const SIGNATURE_PNG_MIN_HEIGHT = 20

/** How long an unlocked (password-checked) session lasts. One sitting. */
export const SIGNATURE_SESSION_TTL_MS = 30 * 60 * 1000

/** What the generation worker advertises in GET /health when it can render a signing master. */
export const WORKER_CAPABILITY_ESIGN = 'esign'

// =============================================================================
// Placeholders
// =============================================================================

export type SignaturePlaceholderKind = 'sign' | 'sign_name' | 'sign_date'

export interface SignaturePlaceholder {
  kind: SignaturePlaceholderKind
  role: string
  /** The filter chain after the role, pipe included (`| date:"DD MMM YYYY"`), or ''. */
  filters: string
}

/**
 * A role is named like a field: letters, digits and underscores in any script.
 * No spaces or dots — the rest of the template pipeline splits on both.
 */
const ROLE = String.raw`[\p{L}\p{N}\p{M}_]{1,60}`
const SIGNATURE_PLACEHOLDER_RE = new RegExp(String.raw`^(SIGN|SIGN_NAME|SIGN_DATE):\s*(${ROLE})\s*(\|[\s\S]*)?$`, 'iu')

/**
 * Read `SIGN:Customer`, `SIGN_NAME:Customer` or `SIGN_DATE:Customer | date:"…"`
 * — the text between the braces. Returns null for anything else.
 *
 * The prefix is case-insensitive, as QR: and BARCODE: are. The role is not: it
 * is a name the sender reads back in a form, and "Customer" and "customer"
 * showing up as two people to assign is how they find the typo.
 *
 * Mirrored in generation-worker/src/docx/signaturePlaceholder.ts (kept local
 * there for Docker build independence) — change both together;
 * signaturePlaceholder.mirror.test.ts fails when they part.
 */
export function parseSignaturePlaceholder(content: string): SignaturePlaceholder | null {
  const match = content.trim().match(SIGNATURE_PLACEHOLDER_RE)
  if (!match) return null
  const kind = match[1].toLowerCase() as SignaturePlaceholderKind
  const filters = (match[3] ?? '').trim()
  // Only text takes filters. A picture's alt text with a pipe is a typo, not a role.
  if (kind === 'sign' && filters) return null
  return { kind, role: match[2], filters }
}

/**
 * The generation worker's "no such field" warning about a `{SIGN:…}`, `{SIGN_NAME:…}` or
 * `{SIGN_DATE:…}` placeholder (`Field "SIGN:Client" not found`). Those are filled when the
 * document is signed, never from the data, so a run that is not signed (the document API
 * without `sign`, a Generate with no signing) must not list them as fields with no value.
 * The worker no longer says it of a `{SIGN:…}` picture (text ones were never read as fields);
 * this drops what an older worker still says, wherever a run's warnings are shown.
 */
export function isSignaturePlaceholderWarning(warning: unknown): boolean {
  const name = missingFieldOfWarning(warning)
  return !!name && parseSignaturePlaceholder(name) !== null
}

/** A run's warnings without the signature-placeholder ones (isSignaturePlaceholderWarning). */
export function withoutSignaturePlaceholderWarnings<T>(warnings: readonly T[]): T[] {
  return warnings.filter((w) => !isSignaturePlaceholderWarning(w))
}

/** True when `role` could appear in a `{SIGN:…}` placeholder. */
export function isValidSignatureRole(role: string): boolean {
  return new RegExp(`^${ROLE}$`, 'u').test(role)
}

/**
 * A template's signature roles — none unless it is a Word file. Only a Word
 * template is kept as a signing master; a `{SIGN:Role}` picture in a workbook
 * or a deck is recorded by the validator but can never be signed, so the
 * button, the settings and the API must not offer it. A template from before
 * formats existed has none, and is Word.
 */
export function templateSignatureRoles(template: { placeholders: string | null | undefined; template_format?: string | null }): string[] {
  return (template.template_format ?? 'docx') === 'docx' ? signatureRolesOf(template.placeholders) : []
}

/**
 * Whether documents from templates of this kind can be sent for signature at all.
 * Only a Base template's: the signing routes read signers from, and write links back
 * to, a Base record. An approval document has no such record; Excel, SharePoint,
 * Dataverse, Google Sheets and api templates wait for signing keyed on the run
 * rather than the record. Absent (a row from before source types) reads as Base.
 */
export function sourceCanBeSigned(sourceType: unknown): boolean {
  return sourceType === undefined || sourceType === null || sourceType === 'base'
}

/**
 * Whether a document from this template can be sent for signature: a Base
 * template's Word file with roles, making one document per record. A combined
 * document is about many records — signers are read from one record and links
 * written back to it — so it is never signable (ESIGN_COMBINED). What the
 * picker's badge, the Send button and the job's `signing` option all ask, so a
 * client whose templates cannot be signed never advertises it.
 */
export function isSignableTemplate(template: { placeholders: string | null | undefined; template_format?: string | null; record_mode?: unknown; source_type?: unknown }): boolean {
  if (!sourceCanBeSigned(template.source_type)) return false
  return !isCombinedMode(effectiveRecordMode(template)) && templateSignatureRoles(template).length > 0
}

/**
 * The signature roles in a template's stored placeholder tree
 * (`templates.placeholders`), in document order. Mind the format: see
 * templateSignatureRoles().
 */
export function signatureRolesOf(placeholdersJson: string | null | undefined): string[] {
  if (!placeholdersJson) return []
  try {
    const nodes = JSON.parse(placeholdersJson) as Array<{ variable?: unknown; fieldType?: unknown }>
    if (!Array.isArray(nodes)) return []
    const roles: string[] = []
    for (const node of nodes) {
      if (node?.fieldType === 'signature' && typeof node.variable === 'string' && !roles.includes(node.variable)) {
        roles.push(node.variable)
      }
    }
    return roles
  } catch {
    return []
  }
}

// =============================================================================
// Entities
// =============================================================================

export type SignatureRequestStatus = 'pending' | 'completed' | 'declined' | 'expired' | 'cancelled'
export type SignatureSignerStatus = 'pending' | 'viewed' | 'signed' | 'declined'
export type SignatureSignerKind = 'lark' | 'external'
export type SignatureRequestOrigin = 'extension' | 'automation'
export type SignatureOrder = 'parallel' | 'sequential'

export type SignatureEventType =
  | 'created'
  | 'viewed'
  | 'password_failed'
  | 'locked'
  | 'signed'
  | 'declined'
  | 'reminded'
  | 'cancelled'
  | 'expired'
  | 'completed'
  | 'delivery_failed'

export interface SignatureRequest {
  id: string
  lark_tenant_key: string
  generation_id: string
  template_id: string
  /** Where the signed PDF is saved back to; copied from the generation run. */
  lark_table_id: string | null
  lark_record_id: string | null
  title: string
  /**
   * As stored. A request past `expires_at` still reads 'pending' here until the
   * nightly cron gets to it — ask signatureRequestStatus(), never this column.
   */
  status: SignatureRequestStatus
  /** lark_user_id of the sender. */
  created_by: string
  signing_order: SignatureOrder
  expires_at: number
  /** PBKDF2 hash and salt, both hex; NULL when the sender set no password. */
  password_hash: string | null
  password_salt: string | null
  /** The frozen, filled DOCX every render starts from. */
  master_r2_key: string
  /** Latest preview PDF and how many signatures it shows. NULL until the first render. */
  preview_r2_key: string | null
  rendered_count: number | null
  final_r2_key: string | null
  /** SHA-256 (hex) of the master: "which exact document" on the audit page. */
  document_sha256: string
  /** Sender's language, for the bot cards and the audit page. */
  locale: string | null
  /** The template's timezone at the time of sending; audit times are printed in it. */
  timezone: string | null
  /** Base name for the signed file, without extension. */
  filename: string
  completed_at: number | null
  /** Set once the files have been swept (SIGNATURE_FILE_RETENTION_DAYS). */
  files_deleted_at: number | null
  /** 'extension' when a person sent it; 'automation' when an HTTP trigger or the API did. */
  origin: SignatureRequestOrigin
  /**
   * JSON SignatureRecordTargets: which text fields of the record receive each
   * role's link and password, so a re-issued link can be written there again.
   */
  record_fields: string | null
  created_at: number
  updated_at: number
}

export interface SignatureSigner {
  id: string
  request_id: string
  role: string
  display_name: string
  kind: SignatureSignerKind
  lark_open_id: string | null
  /** Free text from the sender — an email, a phone number. We do not send to it. */
  contact_note: string | null
  /** 0-based place in the order; only meaningful for a sequential request. */
  position: number
  /** SHA-256 (hex) of the link token. The token itself is shown once and never stored. */
  token_hash: string
  /**
   * A password of this signer's own, generated for them (PBKDF2, hex). When
   * set it is the one that opens this link; otherwise the request's shared
   * password, if any.
   */
  password_hash: string | null
  password_salt: string | null
  status: SignatureSignerStatus
  /** Wrong passwords since the link was issued. */
  failed_attempts: number
  locked_at: number | null
  viewed_at: number | null
  signed_at: number | null
  typed_name: string | null
  signature_r2_key: string | null
  ip: string | null
  user_agent: string | null
  decline_reason: string | null
  created_at: number
}

/** One line of the audit trail. Append-only. */
export interface SignatureEvent {
  id: string
  request_id: string
  signer_id: string | null
  event: SignatureEventType
  at: number
  ip: string | null
  user_agent: string | null
  detail: string | null
}

// =============================================================================
// State
// =============================================================================

/** When a request sent at `createdAt` stops accepting signatures. */
export function signatureExpiresAt(createdAt: number): number {
  return createdAt + SIGNATURE_VALIDITY_DAYS * MS_PER_DAY
}

/**
 * The status of a request as of `now`.
 *
 * The stored column is a cache for the one transition nothing writes at the
 * moment it happens: expiry. The cron marks it at night; until then the row
 * still says 'pending'. Every gate and every screen derives it here instead, so
 * a link stops working the second it is overdue — the same rule as
 * isTemplateLive() and isQuotaEnforced().
 *
 * `everyoneSigned`: a request everybody signed in time is finishing, not
 * expiring, even if its final render failed and the clock ran out before a
 * retry. Calling it expired would let a re-send overwrite a signed contract.
 * Pass it wherever the signers are at hand.
 */
export function signatureRequestStatus(
  request: Pick<SignatureRequest, 'status' | 'expires_at'>,
  now: number = Date.now(),
  everyoneSigned = false,
): SignatureRequestStatus {
  if (request.status === 'pending' && now >= request.expires_at && !everyoneSigned) return 'expired'
  return request.status
}

type SignerTurn = Pick<SignatureSigner, 'id' | 'position' | 'status'>

/** Signers who must sign before `signer` may: none unless the order is sequential. */
export function signersAhead(order: SignatureOrder, signers: SignerTurn[], signer: SignerTurn): number {
  if (order !== 'sequential') return 0
  return signers.filter((s) => s.position < signer.position && s.status !== 'signed').length
}

/**
 * Signers who may sign now and have not: everyone unsigned for a parallel
 * request, the single next one for a sequential request.
 */
export function signersInTurn<T extends SignerTurn>(order: SignatureOrder, signers: T[]): T[] {
  const open = signers.filter((s) => s.status === 'pending' || s.status === 'viewed')
  if (order !== 'sequential') return open
  const next = [...open].sort((a, b) => a.position - b.position)[0]
  return next && signersAhead(order, signers, next) === 0 ? [next] : []
}

/** True once every signer has signed. */
export function allSigned(signers: Array<Pick<SignatureSigner, 'status'>>): boolean {
  return signers.length > 0 && signers.every((s) => s.status === 'signed')
}

/**
 * What a link shows.
 *
 * - `ready`    may read the document and sign or decline
 * - `locked`   password protected and not yet unlocked in this session
 * - `blocked`  too many wrong passwords; the sender must re-issue the link
 * - `waiting`  a sequential request, and it is someone else's turn
 * - `signed`   this signer has signed and others have not
 * - the rest   the request is closed; nothing but that fact is revealed
 */
export type SigningLinkState =
  | 'ready'
  | 'locked'
  | 'blocked'
  | 'waiting'
  | 'signed'
  | 'completed'
  | 'declined'
  | 'expired'
  | 'cancelled'

/**
 * The state of one signer's link.
 *
 * Closed requests come first and win over everything: a token for an expired,
 * cancelled, declined or completed request reveals only that, whatever the
 * signer had done before. A password comes next, even for a signer who has
 * already signed: until it is given, a link reveals nothing about the document
 * — not even that its holder signed it.
 */
export function signingLinkState(
  request: Pick<SignatureRequest, 'status' | 'expires_at' | 'signing_order' | 'password_hash'>,
  signers: SignerTurn[],
  signer: SignerTurn & Pick<SignatureSigner, 'locked_at'>,
  unlocked: boolean,
  now: number = Date.now(),
): SigningLinkState {
  const status = signatureRequestStatus(request, now, allSigned(signers))
  if (status !== 'pending') return status
  if (signer.locked_at !== null) return 'blocked'
  if (request.password_hash && !unlocked) return 'locked'
  if (signer.status === 'signed') return 'signed'
  if (signersAhead(request.signing_order, signers, signer) > 0) return 'waiting'
  return 'ready'
}

// =============================================================================
// API shapes
// =============================================================================

/** One role's assignment in POST /api/signatures. */
export interface SignatureSignerInput {
  role: string
  kind: SignatureSignerKind
  /** Lark signer: their lark_user_id (open_id). */
  larkUserId?: string
  /** External signer: the name the sender types. */
  name?: string
  note?: string
}

export interface CreateSignatureRequestBody {
  generationId: string
  signers: SignatureSignerInput[]
  order: SignatureOrder
  /** One password, typed by the sender, shared by every signer. */
  password?: string
  /**
   * Instead: a strong password generated for each signer outside the company,
   * shown once with their link. Cannot be combined with `password`.
   */
  generatePasswords?: boolean
  /** The sender's UI language; the bot cards and the audit page use it. */
  locale?: string
}

/** A signer as the sender sees them. Never includes the token hash. */
export interface SignatureSignerSummary {
  id: string
  role: string
  displayName: string
  kind: SignatureSignerKind
  contactNote: string | null
  position: number
  status: SignatureSignerStatus
  locked: boolean
  signedAt: number | null
  typedName: string | null
  declineReason: string | null
}

export interface SignatureRequestSummary {
  id: string
  title: string
  /** Derived as of the response — see signatureRequestStatus(). */
  status: SignatureRequestStatus
  order: SignatureOrder
  createdAt: number
  expiresAt: number
  completedAt: number | null
  createdBy: string
  createdByName: string | null
  hasPassword: boolean
  signers: SignatureSignerSummary[]
  /** False once the files have been swept; the audit trail outlives them. */
  filesAvailable: boolean
}

export interface SignatureEventSummary {
  event: SignatureEventType
  at: number
  signerId: string | null
  ip: string | null
  userAgent: string | null
  detail: string | null
}

/** A link, shown to the sender once. It is the signer's credential. */
export interface SignatureLink {
  signerId: string
  role: string
  displayName: string
  url: string
  /** The signer's generated password — present only the one time it is shown. */
  password?: string
}

export interface CreateSignatureRequestResponse {
  request: SignatureRequestSummary
  /** One per external signer, and per Lark signer the bot could not reach. */
  links: SignatureLink[]
}

export interface ListSignatureRequestsResponse {
  requests: SignatureRequestSummary[]
  /** Added with `?page&limit&q&status`; optional so an older API still type-checks. */
  pagination?: ListPagination
}

export interface GetSignatureRequestResponse {
  request: SignatureRequestSummary
  events: SignatureEventSummary[]
}

export interface RemindSignerResponse {
  /** Lark signer: whether the bot message went out. */
  sent: boolean
  /** The new link, when the sender has to pass it on by hand. The old one is dead. */
  link: SignatureLink | null
}

/** GET /api/signatures/generations/:generationId — may this document be sent, and to which roles. */
export interface SignatureEligibilityResponse {
  eligible: boolean
  roles: string[]
  /** Why not, as a code the UI translates. */
  reason?: SignatureErrorCode
  /**
   * Everyone already signed this document once (a completed request). Sending
   * it again is allowed, but the sender is asked first.
   */
  alreadySigned?: boolean
}

export interface SignatureColleague {
  larkUserId: string
  name: string | null
}

export interface ListSignatureColleaguesResponse {
  colleagues: SignatureColleague[]
}

/** GET /api/sign/:token. For a closed request only `state` is present. */
export interface SigningLinkInfo {
  state: SigningLinkState
  title?: string
  companyName?: string | null
  role?: string
  signerName?: string
  passwordRequired?: boolean
  expiresAt?: number
  signedCount?: number
  totalSigners?: number
  /** `waiting` only: how many people sign first. Never who. */
  waitingFor?: number
  /** `locked` and `blocked`: wrong passwords left before the link is blocked. */
  attemptsLeft?: number
}

export interface UnlockSigningLinkResponse {
  session: string
  expiresAt: number
}

export interface SignDocumentBody {
  typedName: string
  /** `data:image/png;base64,…` */
  signaturePng: string
  consent: true
  session?: string
}

export interface SignDocumentResponse {
  state: 'signed' | 'completed'
  signedCount: number
  totalSigners: number
  /** Short-lived link to the PDF as it now stands; null if it is still being prepared. */
  downloadUrl: string | null
}

export interface DeclineDocumentBody {
  reason?: string
  session?: string
}

/** Every refusal the signature routes give, authenticated and public. */
export type SignatureErrorCode =
  | 'ESIGN_WORKER_UNSUPPORTED'
  | 'ESIGN_WORKER_UNAVAILABLE'
  | 'ESIGN_GENERATION_NOT_FOUND'
  | 'ESIGN_NO_MASTER'
  | 'ESIGN_ROLES_MISMATCH'
  | 'ESIGN_INVALID_SIGNER'
  | 'ESIGN_INVALID_PASSWORD'
  | 'ESIGN_ALREADY_PENDING'
  | 'ESIGN_NOT_FOUND'
  | 'ESIGN_SIGNER_NOT_FOUND'
  | 'ESIGN_NOT_PENDING'
  | 'ESIGN_FILES_GONE'
  | 'ESIGN_RENDER_FAILED'
  | 'ESIGN_SIGNER_FIELD'
  | 'ESIGN_RECORD_UNREADABLE'
  | 'ESIGN_SETTINGS_CONFLICT'
  | 'ESIGN_GENERATION_FAILED'
  | 'ESIGN_COMBINED'
  | 'SIGN_NOT_FOUND'
  | 'SIGN_CLOSED'
  | 'SIGN_LOCKED'
  | 'SIGN_BLOCKED'
  | 'SIGN_WRONG_PASSWORD'
  | 'SIGN_NOT_YOUR_TURN'
  | 'SIGN_ALREADY_SIGNED'
  | 'SIGN_INVALID_SIGNATURE'
  | 'SIGN_INVALID_NAME'
  | 'SIGN_CONSENT_REQUIRED'
  | 'SIGN_RATE_LIMITED'
  | 'SIGN_DOCUMENT_UNAVAILABLE'

/**
 * The i18n key for a refusal, for both sender UIs and the signing page.
 *
 * One table so the three surfaces cannot word the same refusal differently.
 * The keys live in shared-ui's locales under the `signError` prefix.
 */
const SIGNATURE_ERROR_KEYS: Record<SignatureErrorCode, string> = {
  ESIGN_WORKER_UNSUPPORTED: 'signErrorWorkerUnsupported',
  ESIGN_WORKER_UNAVAILABLE: 'signErrorUnavailable',
  ESIGN_GENERATION_NOT_FOUND: 'signErrorGenerationNotFound',
  ESIGN_NO_MASTER: 'signErrorNoMaster',
  ESIGN_ROLES_MISMATCH: 'signErrorRolesMismatch',
  ESIGN_INVALID_SIGNER: 'signErrorInvalidSigner',
  ESIGN_INVALID_PASSWORD: 'signErrorInvalidPassword',
  ESIGN_ALREADY_PENDING: 'signErrorAlreadyPending',
  ESIGN_NOT_FOUND: 'signErrorRequestNotFound',
  ESIGN_SIGNER_NOT_FOUND: 'signErrorSignerNotFound',
  ESIGN_NOT_PENDING: 'signErrorNotPending',
  ESIGN_FILES_GONE: 'signErrorFilesGone',
  ESIGN_RENDER_FAILED: 'signErrorUnavailable',
  ESIGN_SIGNER_FIELD: 'signErrorSignerField',
  ESIGN_RECORD_UNREADABLE: 'signErrorRecordUnreadable',
  ESIGN_SETTINGS_CONFLICT: 'signErrorSettingsConflict',
  ESIGN_GENERATION_FAILED: 'signErrorGenerationFailed',
  ESIGN_COMBINED: 'signErrorCombined',
  SIGN_NOT_FOUND: 'signErrorLinkNotFound',
  SIGN_CLOSED: 'signErrorClosed',
  SIGN_LOCKED: 'signErrorLocked',
  SIGN_BLOCKED: 'signErrorBlocked',
  SIGN_WRONG_PASSWORD: 'signErrorWrongPassword',
  SIGN_NOT_YOUR_TURN: 'signErrorNotYourTurn',
  SIGN_ALREADY_SIGNED: 'signErrorAlreadySigned',
  SIGN_INVALID_SIGNATURE: 'signErrorInvalidSignature',
  SIGN_INVALID_NAME: 'signErrorInvalidName',
  SIGN_CONSENT_REQUIRED: 'signErrorConsentRequired',
  SIGN_RATE_LIMITED: 'signErrorRateLimited',
  SIGN_DOCUMENT_UNAVAILABLE: 'signErrorUnavailable',
}

export function signatureErrorMessageKey(code: string | undefined | null): string {
  return (code && SIGNATURE_ERROR_KEYS[code as SignatureErrorCode]) || 'signErrorGeneric'
}

// =============================================================================
// Automation: starting a request with no person in the extension
// =============================================================================

/** Length of a generated signer password. 16 characters of 56 is about 93 bits. */
export const SIGNATURE_GENERATED_PASSWORD_LENGTH = 16

/**
 * Where one role's signer, link and password live on the record. Field
 * *names*, like a template's save and status fields — the worker writes those
 * by name, and an automation author types a name, not an id.
 */
export interface SignatureRoleFields {
  /** A User field → a colleague, asked by bot. A text or email field → someone outside, by link. */
  signerField?: string
  /** A text field that receives this role's signing link. */
  linkField?: string
  /** A text field that receives this role's generated password. */
  passwordField?: string
}

/** A template's e-signature automation settings (`templates.signature_config`). */
export interface SignatureAutomationConfig {
  roles: Record<string, SignatureRoleFields>
  order?: SignatureOrder
}

/** GET/PUT /api/signatures/templates/:templateId/settings */
export interface SignatureSettingsResponse {
  roles: string[]
  config: SignatureAutomationConfig
}

/** What a request remembers of the above, to write a re-issued link back (`record_fields`). */
export type SignatureRecordTargets = Record<string, Pick<SignatureRoleFields, 'linkField' | 'passwordField'>>

const FIELD_NAME_MAX_LENGTH = 100

function cleanFieldName(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const name = value.trim()
  return name && name.length <= FIELD_NAME_MAX_LENGTH ? name : undefined
}

/**
 * Read stored or submitted automation settings, keeping only what is
 * well-formed and, when `roles` is given, only roles the template has. Never
 * throws: a malformed config is an empty one.
 */
export function parseSignatureAutomationConfig(input: unknown, roles?: string[]): SignatureAutomationConfig {
  let raw: unknown = input
  if (typeof input === 'string') {
    try {
      raw = JSON.parse(input)
    } catch {
      raw = null
    }
  }
  const config: SignatureAutomationConfig = { roles: {} }
  if (!raw || typeof raw !== 'object') return config
  const source = raw as { roles?: unknown; order?: unknown }
  if (source.order === 'sequential' || source.order === 'parallel') config.order = source.order
  if (source.roles && typeof source.roles === 'object') {
    for (const [role, fields] of Object.entries(source.roles as Record<string, unknown>)) {
      if (!isValidSignatureRole(role) || (roles && !roles.includes(role)) || !fields || typeof fields !== 'object') continue
      const f = fields as Record<string, unknown>
      const entry: SignatureRoleFields = {}
      const signerField = cleanFieldName(f.signerField)
      const linkField = cleanFieldName(f.linkField)
      const passwordField = cleanFieldName(f.passwordField)
      if (signerField) entry.signerField = signerField
      if (linkField) entry.linkField = linkField
      if (passwordField) entry.passwordField = passwordField
      if (Object.keys(entry).length > 0) config.roles[role] = entry
    }
  }
  return config
}

/**
 * Who signs as a role, when a request is started by automation:
 * - `{ field }`      a field of the generated record — a User field is a
 *                    colleague, a text or email field someone outside;
 * - `{ larkUserId }` a colleague, by open_id — must be a user of the tenant;
 * - `{ name }`       someone outside, by name (and a contact note).
 */
export type AutomatedSignerSource =
  | { field: string }
  | { larkUserId: string }
  | { name: string; contact?: string }

/** Input of startAutomatedSignatureRequest() / queueAutomatedSignatureRequest() in the API worker. */
export interface AutomatedSignatureInput {
  /** A completed generation of a Base template of the caller's tenant. */
  generationId: string
  /** When given, the generation must come from this template — bind it to the credential's own template. */
  templateId?: string
  /** Role → signer. A role left out falls back to the template's settings (`signerField`). */
  signers?: Record<string, AutomatedSignerSource>
  /** Defaults to the template's setting, then parallel. */
  order?: SignatureOrder
  /** A generated password for each outside signer. Default true. */
  generatePasswords?: boolean
  /** Language of the bot cards and the audit page. Default English. */
  locale?: string
}

export interface AutomatedSignatureResult {
  request: SignatureRequestSummary
  /**
   * Every outside signer's link, with its generated password, and the link of
   * any colleague the bot could not reach. Shown once: nothing can show them again.
   */
  links: SignatureLink[]
  /** The record fields written (link and password), by name. */
  fieldsWritten: string[]
  /** Things that did not stop the request: an unwritable field, a second user in a User field. */
  warnings: string[]
}

/** A queued automation, as recorded on the generation run (`generation_runs.signing_request`). */
export interface AutomatedSignatureOutcome {
  /** `starting` while one completion callback is starting it — what stops a retried callback starting a second. */
  status: 'queued' | 'starting' | 'started' | 'failed'
  requestId?: string
  error?: SignatureErrorCode
  /** What did not stop the request, e.g. a second person in a User field. */
  warnings?: string[]
}

// =============================================================================
// Worker protocol
// =============================================================================

/** One collected signature, as the generation worker needs it to render. */
export interface SigningRenderSignature {
  role: string
  typedName: string
  signedAt: number
  /** PNG, base64. Absent prints the name and date but leaves "sign here". */
  imageBase64?: string
}

/**
 * The audit page, already worded. The API builds it in the sender's language;
 * the worker only lays it out, so a new language never needs a worker release.
 */
export interface SigningAuditPage {
  title: string
  /** Label/value lines about the document. */
  fields: Array<{ label: string; value: string }>
  /** One block per signer. */
  signers: Array<{ heading: string; fields: Array<{ label: string; value: string }> }>
  footer: string
}

/** POST /signing/render on the generation worker. */
export interface SigningRenderRequest {
  masterBase64: string
  /** Signatures collected so far. Roles not listed keep their "sign here" picture. */
  signatures: SigningRenderSignature[]
  /** IANA timezone `{SIGN_DATE:…}` prints in when its filter names none. */
  timezone?: string
  /** Final render only: appended as the last page. */
  auditPage?: SigningAuditPage
}

export interface SigningRenderResponse {
  success: boolean
  pdfBase64?: string
  /** Signature roles found in the master, in document order. */
  roles?: string[]
  warnings?: string[]
  error?: string
}

/** POST /signing/deliver on the generation worker: write the outcome onto the record. */
export interface SigningDeliverRequest {
  /** A `lark` data source with the template's save and status fields. */
  dataSource: import('./worker').LarkDataSourceConfig
  recordId: string
  /** Text for the status field, if the template has one. */
  statusMessage?: string
  /** The signed PDF for the attachment field, if the template has one. */
  file?: { filename: string; base64: string }
}

export interface SigningDeliverResponse {
  success: boolean
  warnings?: string[]
  error?: string
}
